import { createBrowserRouter, useParams, redirect } from "react-router-dom";
import { lazy } from "react";
import Layout from "./Layout";
import NotFound from "../features/static/NotFound";
import { ROUTING_PATH } from "../core/config/Constant";
import useAuthStore from "../core/store/authStore";

// Route loader: blocks the route from rendering if the user isn't logged in.
// Runs before component mount, so there's no flash of unauthenticated UI.
const requireAuth = () => {
  if (!useAuthStore.getState().isLoggedIn) {
    return redirect(ROUTING_PATH.HOME);
  }
  return null;
};

const Home = lazy(() => import("../features/home/Home"));
const UnifiedPropertyDashboard = lazy(() => import("../features/properties/pages/UnifiedPropertyDashboard"));
const Offer = lazy(() => import("../features/offers/pages/Offer"));
const Detail = lazy(() => import("../features/properties/pages/Detail"));
const Download = lazy(() => import("../features/home/Download"));
const OfferDetail = lazy(() => import("../features/offers/pages/OfferDetail"));
const Dashboard = lazy(() => import("../features/dashboard/pages/Dashboard"));
const AddNewProperty = lazy(() => import("../features/properties/forms/AddNewProperty"));
const EditProperty = lazy(() => import("../features/properties/forms/EditProperty"));
const AddNewProject = lazy(() => import("../features/projects/forms/AddNewProject"));
const Editproject = lazy(() => import("../features/projects/forms/Editproject"));
const AddNewOffer = lazy(() => import("../features/offers/forms/AddNewOffer"));
const ProjectDetail = lazy(() => import("../features/projects/pages/ProjectDetail"));
const AgentDetail = lazy(() => import("../features/agents/pages/AgentDetail"));
const ProjectList = lazy(() => import("../features/projects/pages/ProjectList"));
const AllOffers = lazy(() => import("../features/offers/pages/AllOffers"));
const AboutUs = lazy(() => import("../features/static/AboutUs"));
const Privacy = lazy(() => import("../features/static/Privacy"));
const Terms = lazy(() => import("../features/static/Terms"));
const FAQ = lazy(() => import("../features/static/FAQ"));
const ContactUs = lazy(() => import("../features/static/ContactUs"));
const AccountDeletion = lazy(() => import("../features/static/AccountDeletion"));

const PropertyDetailRoute = () => { const { _id } = useParams(); return <Detail key={_id} />; };
const ProjectDetailRoute  = () => { const { _id } = useParams(); return <ProjectDetail key={_id} />; };

const relative = (path) => path.replace(/^\//, '');

const router = createBrowserRouter([
  {
    path: ROUTING_PATH.HOME,
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: relative(ROUTING_PATH.DASHBOARD), loader: requireAuth, element: <Dashboard /> },
      { path: relative(ROUTING_PATH.ADD_NEW_PROPERTY), loader: requireAuth, element: <AddNewProperty /> },
      { path: relative(ROUTING_PATH.EDIT_PROPERTY) + "/:_id", loader: requireAuth, element: <EditProperty /> },
      { path: relative(ROUTING_PATH.ADD_NEW_PROJECT), loader: requireAuth, element: <AddNewProject /> },
      { path: relative(ROUTING_PATH.EDIT_PROJECT) + "/:_id", loader: requireAuth, element: <Editproject /> },
      { path: relative(ROUTING_PATH.ADD_NEW_OFFER), loader: requireAuth, element: <AddNewOffer /> },
      { path: relative(ROUTING_PATH.PROPERTY), element: <UnifiedPropertyDashboard /> },
      { path: "properties", element: <UnifiedPropertyDashboard /> },
      { path: relative(ROUTING_PATH.PROJECT_DETAIL) + "/:_id", element: <ProjectDetailRoute /> },
      { path: relative(ROUTING_PATH.ALL_OFFERS), element: <AllOffers /> },
      { path: relative(ROUTING_PATH.OFFER_DETAIL) + "/:_id", element: <OfferDetail /> },
      { path: relative(ROUTING_PATH.ABOUT_US), element: <AboutUs /> },
      { path: relative(ROUTING_PATH.PRIVACY_POLICY), element: <Privacy /> },
      { path: relative(ROUTING_PATH.TERMS_AND_CONDITIONS), element: <Terms /> },
      { path: relative(ROUTING_PATH.FAQ), element: <FAQ /> },
      { path: relative(ROUTING_PATH.CONTACT), element: <ContactUs /> },
      { path: relative(ROUTING_PATH.ACCOUNT_DELETION), element: <AccountDeletion /> },
      { path: relative(ROUTING_PATH.OFFER) + "/:_id", element: <Offer /> },
      { path: relative(ROUTING_PATH.PROPERTY_DETAILS) + "/:_id", element: <PropertyDetailRoute /> },
      { path: relative(ROUTING_PATH.DOWNLOAD), element: <Download /> },
      { path: relative(ROUTING_PATH.AGENT_DETAIL) + "/:_id", element: <AgentDetail /> },
      { path: relative(ROUTING_PATH.PROJECT_LIST), element: <ProjectList /> },
      { path: "*", element: <NotFound /> }
    ]
  }
]);

export default router;